/**
 * The web header's search (components/common/HeaderSearch.tsx): every page a parent can open,
 * found by its name or by the everyday words parents use for it - "pay" finds Fees, Wallet
 * and Canteen, "sick" finds Leaves. Pages only: switching child stays with the header's own
 * child switcher (owner, 2 Nov 2026).
 *
 * No React here, so the matching is tested in Node.
 */

export interface PortalPage {
  /** The web navigator's route (navigation/AppNavigator.web.tsx). */
  route: string;
  title: string;
  subtitle: string;
  /** A Feather icon name. */
  icon: string;
  /** The icon tile's colours - the same pairs as the dashboard's quick-action tiles. */
  tint: string;
  color: string;
  /** Other words parents use for the page, space-separated. */
  keywords: string;
}

export interface SearchGroup {
  title: string;
  pages: PortalPage[];
}

const BLUE = { tint: '#DBEAFE', color: '#1B70D1' };
const SKY = { tint: '#E0F2FE', color: '#0284C7' };
const INDIGO = { tint: '#E0E7FF', color: '#4F46E5' };
const VIOLET = { tint: '#EDE9FE', color: '#7C3AED' };
const AMBER = { tint: '#FEF3C7', color: '#D97706' };
const GREEN = { tint: '#DCFCE7', color: '#16A34A' };
const EMERALD = { tint: '#D1FAE5', color: '#059669' };
const ORANGE = { tint: '#FFEDD5', color: '#EA580C' };
const RED = { tint: '#FEE2E2', color: '#DC2626' };
const PINK = { tint: '#FCE7F3', color: '#DB2777' };
const SLATE = { tint: '#F1F5F9', color: '#475569' };

/**
 * Pages the web portal routes to and shows somewhere (sidebar, dashboard, More). Documents
 * and Transport are left out while they are hidden from the sidebar; the CXC page is added
 * only while registration is open (portalPages). There is no Assignments page: that work is
 * the Assessments tab on Academics now (owner, 2 Nov 2026).
 */
const PAGES: PortalPage[] = [
  { route: 'Home', title: 'Dashboard', subtitle: 'Today at a glance', icon: 'home', ...BLUE, keywords: 'home overview start summary' },
  { route: 'Fees', title: 'Fees and payments', subtitle: 'Pay fees and see receipts', icon: 'credit-card', ...BLUE, keywords: 'pay payment fee bill invoice due receipt installment tuition money' },
  { route: 'Wallet', title: 'Wallet', subtitle: 'Top up and pay bills', icon: 'dollar-sign', ...ORANGE, keywords: 'money balance topup card' },
  { route: 'Canteen', title: 'Canteen', subtitle: 'Order and pay for meals', icon: 'coffee', ...RED, keywords: 'lunch food meal menu order snack eat breakfast' },
  { route: 'Store', title: 'Store', subtitle: 'Uniforms, books and supplies', icon: 'shopping-bag', ...PINK, keywords: 'buy shop uniform books stationery' },
  { route: 'Attendance', title: 'Attendance', subtitle: 'Daily and monthly reports', icon: 'calendar', ...AMBER, keywords: 'absent present late register days' },
  { route: 'Leaves', title: 'Leaves', subtitle: 'Apply for leave and see history', icon: 'file-text', ...GREEN, keywords: 'sick ill holiday absence off doctor vacation' },
  { route: 'EarlyPickup', title: 'Early pickup', subtitle: 'Request an early release', icon: 'log-out', ...EMERALD, keywords: 'collect appointment release dismissal' },
  { route: 'ExamResults', title: 'Exam results', subtitle: 'Marks and report cards', icon: 'bookmark', ...INDIGO, keywords: 'marks grades score report card test performance' },
  { route: 'Academics', title: 'Academics', subtitle: 'Homework, projects, exams and assessments', icon: 'book-open', ...INDIGO, keywords: 'study subjects lessons school work timetable assessment' },
  { route: 'Homework', title: 'Homework', subtitle: "What's due and when", icon: 'book', ...VIOLET, keywords: 'task worksheet due' },
  { route: 'Projects', title: 'Projects', subtitle: 'Longer pieces of work', icon: 'folder', ...VIOLET, keywords: 'group coursework' },
  { route: 'Timetable', title: 'Timetable', subtitle: "Today's classes", icon: 'grid', ...SKY, keywords: 'schedule class period subject teacher lesson' },
  { route: 'Communication', title: 'Communication', subtitle: 'Messages and school events', icon: 'message-square', ...SKY, keywords: 'inbox news contact email' },
  { route: 'Chat', title: 'Messages', subtitle: 'Chat with teachers', icon: 'message-circle', ...SKY, keywords: 'chat teacher reply talk' },
  { route: 'Notifications', title: 'Notifications', subtitle: 'Alerts from the school', icon: 'bell', ...AMBER, keywords: 'alert news updates' },
  { route: 'Events', title: 'Events', subtitle: 'The school calendar', icon: 'flag', ...PINK, keywords: 'trip sports day holiday calendar' },
  { route: 'Complaints', title: 'Complaints', subtitle: 'Raise a concern', icon: 'alert-circle', ...RED, keywords: 'concern issue problem bus bullying' },
  { route: 'Profile', title: 'Student profile', subtitle: 'Details, contacts and photo', icon: 'user', ...SKY, keywords: 'details address contact emergency photo personal information' },
  { route: 'StudentId', title: 'Student ID', subtitle: 'Digital ID card', icon: 'user-check', ...SLATE, keywords: 'card badge barcode' },
  { route: 'Settings', title: 'Settings', subtitle: 'Notifications, dark mode and account', icon: 'settings', ...SLATE, keywords: 'preferences password theme account' },
];

const CXC_PAGE: PortalPage = {
  route: 'CxcRegistration', title: 'CXC registration', subtitle: 'Register for this sitting', icon: 'award', ...AMBER,
  keywords: 'csec exam register sitting entry',
};

/** Shown on an empty search before the parent has opened anything from it. */
const SUGGESTED = ['Fees', 'Attendance', 'Homework', 'Canteen'];

/** The pages a parent can search right now. */
export function portalPages({ cxcOpen = false }: { cxcOpen?: boolean } = {}): PortalPage[] {
  return cxcOpen ? [...PAGES, CXC_PAGE] : PAGES;
}

const termsOf = (query: string) => query.trim().toLowerCase().split(/\s+/).filter(Boolean);
const wordsOf = (text: string) => text.toLowerCase().split(/[^a-z0-9$]+/).filter(Boolean);

/** How well one term matches: its title first, then its other words; 0 is no match. */
function termScore(term: string, title: string, subtitle: string, keywords: string): number {
  const t = title.toLowerCase();
  if (t.startsWith(term)) return 4;
  if (wordsOf(title).some(w => w.startsWith(term))) return 3;
  if (t.includes(term)) return 2;
  if (wordsOf(keywords).some(w => w.startsWith(term)) || wordsOf(subtitle).some(w => w.startsWith(term))) return 1;
  return 0;
}

/** Every term must match somewhere; the best matches come first. 0 is no match. */
export function matchScore(query: string, title: string, subtitle = '', keywords = ''): number {
  const terms = termsOf(query);
  if (!terms.length) return 0;
  let total = 0;
  for (const term of terms) {
    const score = termScore(term, title, subtitle, keywords);
    if (!score) return 0;
    total += score;
  }
  return total;
}

/** Best first; equal scores keep their catalogue order. */
function ranked<T>(items: T[], score: (item: T) => number): T[] {
  return items
    .map((item, index) => ({ item, index, score: score(item) }))
    .filter(entry => entry.score > 0)
    .sort((a, b) => b.score - a.score || a.index - b.index)
    .map(entry => entry.item);
}

export interface SearchContext {
  pages: PortalPage[];
  /** Routes opened from the search, most recent first (rememberRecent). */
  recent: string[];
  /** The best few pages only, so the list never needs its own scrollbar. */
  maxPages?: number;
}

/**
 * What the search shows for `query`. Empty: recently opened pages, or a few suggestions
 * before anything has been opened. Otherwise: matching pages, best first. A group with
 * nothing in it is left out, so no groups means nothing matched.
 */
export function searchPortal(query: string, { pages, recent, maxPages = 8 }: SearchContext): SearchGroup[] {
  const byRoute = new Map(pages.map(page => [page.route, page]));
  const known = (routes: string[]) => routes.map(route => byRoute.get(route)).filter((page): page is PortalPage => !!page);

  let group: SearchGroup;
  if (!termsOf(query).length) {
    const opened = known(recent);
    group = opened.length ? { title: 'Recent', pages: opened } : { title: 'Suggested', pages: known(SUGGESTED) };
  } else {
    const found = ranked(pages, page => matchScore(query, page.title, page.subtitle, page.keywords));
    group = { title: 'Pages', pages: found.slice(0, maxPages) };
  }
  return group.pages.length ? [group] : [];
}

/** The results in the order the arrow keys move through them. */
export function flattenResults(groups: SearchGroup[]): PortalPage[] {
  return groups.flatMap(group => group.pages);
}

export interface TextPart {
  text: string;
  match: boolean;
}

/**
 * `text` split into the parts that match a query term (to highlight) and the rest. Matches
 * are found at the start of words, as the search finds them.
 */
export function highlightParts(text: string, query: string): TextPart[] {
  const terms = termsOf(query);
  const lower = text.toLowerCase();
  const marked = new Array<boolean>(text.length).fill(false);
  for (const term of terms) {
    let from = 0;
    while (from <= lower.length - term.length) {
      const at = lower.indexOf(term, from);
      if (at < 0) break;
      // Only where a word starts, so "pay" lights up "payments" but not "repay".
      if (at === 0 || !/[a-z0-9]/i.test(lower[at - 1])) {
        marked.fill(true, at, at + term.length);
        break;
      }
      from = at + 1;
    }
  }
  const parts: TextPart[] = [];
  for (let i = 0; i < text.length; i++) {
    const last = parts[parts.length - 1];
    if (last && last.match === marked[i]) last.text += text[i];
    else parts.push({ text: text[i], match: marked[i] });
  }
  return parts;
}

/** The recent list after opening `route`: it moves to the front, and only the last few stay. */
export function rememberRecent(recent: string[], route: string, max = 4): string[] {
  return [route, ...recent.filter(r => r !== route)].slice(0, max);
}
